"use client";

import { useState } from "react";
import {
  CopyButton,
  DataTable,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import {
  CARD,
  RESULT_CARD,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  type CreditUnit,
  convertCredits,
  DEFAULT_ECTS_PER_US_CREDIT,
  ECTS_HOURS_MAX,
  ECTS_HOURS_MIN,
  roundUsCredits,
  shareOfYear,
  UK_HOURS_PER_CREDIT,
  US_HOURS_PER_SEMESTER_CREDIT,
  workloadHours,
} from "@/lib/tool-kit/intl-grades/ects-credits";

/** English strings. A translated content file overrides these through `ui`. */
export const ECTS_CREDIT_CONVERTER_STRINGS = {
  fromLabel: "Convert from",
  units: {
    ects: "ECTS credits",
    us: "US semester credits",
    uk: "UK credits (CATS)",
  },
  amountLabel: "Number of credits",
  ratioLabel: "ECTS credits per US semester credit",
  ratios: {
    "2": "2 ECTS = 1 US credit (the common convention)",
    custom: "Custom ratio — type it below",
  },
  ratioHint:
    "No rule fixes this. Michigan, TCNJ and Washington and Lee all use 2 ECTS per US credit; your receiving university may not.",
  customRatioLabel: "Custom ECTS per US credit",
  roundingLabel: "Whole-credit rounding for the US result",
  roundings: {
    exact: "Keep fractions (2.5 stays 2.5)",
    up: "Round up to a whole credit",
    down: "Round down to a whole credit",
  },
  results: "Result",
  ectsCard: "ECTS credits",
  usCard: "US semester credits",
  ukCard: "UK credits",
  usRoundedNote:
    "Unrounded: {exact}. The receiving university decides the rounding.",
  yearShare: "{percent} of a full-time academic year (60 ECTS, 120 UK credits)",
  workloadHeading: "Estimated workload",
  colSystem: "Credit system",
  colRule: "Hours per credit",
  colHours: "Hours for this amount",
  rowEcts: "ECTS",
  rowUk: "UK",
  rowUs: "US",
  ruleEcts: "{min}–{max} hours",
  ruleUk: "{hours} notional hours",
  ruleUs: "{hours} hours (1 in class + 2 outside, 15 weeks)",
  hoursRange: "{min}–{max}",
  workloadNote:
    "The three systems count different things, so the hours differ even when the credits are 'equal'. ECTS estimates the typical total workload; the UK figure is notional learning time; the US figure is the federal minimum benchmark.",
  invalid: "Enter a credit amount of zero or more and a ratio above zero.",
  copyResult: "Copy result",
  copied: "Copied!",
  summary: "{ects} ECTS = {us} US semester credits = {uk} UK credits",
} as const;

type Strings = typeof ECTS_CREDIT_CONVERTER_STRINGS;
type Format = ReturnType<typeof useToolNumber>;
type RatioChoice = keyof Strings["ratios"];
type Rounding = keyof Strings["roundings"];

const UNITS: readonly CreditUnit[] = ["ects", "us", "uk"];
const ROUNDINGS: readonly Rounding[] = ["exact", "up", "down"];
const FRACTION = { maximumFractionDigits: 2 } as const;

function workloadRows(
  strings: Strings,
  fmt: Format,
  hours: ReturnType<typeof workloadHours>
) {
  return [
    [
      strings.rowEcts,
      t(strings.ruleEcts, {
        min: fmt(ECTS_HOURS_MIN),
        max: fmt(ECTS_HOURS_MAX),
      }),
      t(strings.hoursRange, {
        min: fmt(hours.ectsMin, FRACTION),
        max: fmt(hours.ectsMax, FRACTION),
      }),
    ],
    [
      strings.rowUk,
      t(strings.ruleUk, { hours: fmt(UK_HOURS_PER_CREDIT) }),
      fmt(hours.uk, FRACTION),
    ],
    [
      strings.rowUs,
      t(strings.ruleUs, { hours: fmt(US_HOURS_PER_SEMESTER_CREDIT) }),
      fmt(hours.us, FRACTION),
    ],
  ];
}

export default function EctsCreditConverterWidget() {
  const strings = useToolUi(ECTS_CREDIT_CONVERTER_STRINGS);
  const fmt = useToolNumber();
  const [from, setFrom] = useState<CreditUnit>("ects");
  const [amountRaw, setAmountRaw] = useState("6");
  const [ratioChoice, setRatioChoice] = useState<RatioChoice>("2");
  const [customRaw, setCustomRaw] = useState("2");
  const [rounding, setRounding] = useState<Rounding>("exact");

  const amount = parseNumberInput(amountRaw);
  const ratio =
    ratioChoice === "custom"
      ? parseNumberInput(customRaw)
      : DEFAULT_ECTS_PER_US_CREDIT;
  const credits =
    amount === null || ratio === null
      ? null
      : convertCredits(amount, from, ratio);

  const usShown = credits ? roundUsCredits(credits.us, rounding) : 0;

  return (
    <div className="space-y-6">
      <div className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.fromLabel}
          name="ects-from"
          onChange={setFrom}
          options={UNITS.map((value) => ({
            value,
            label: strings.units[value],
          }))}
          value={from}
        />
        <TextField
          id="ects-amount"
          label={strings.amountLabel}
          numeric
          onChange={setAmountRaw}
          value={amountRaw}
        />
        <SelectField
          hint={strings.ratioHint}
          id="ects-ratio"
          label={strings.ratioLabel}
          onChange={setRatioChoice}
          options={(Object.keys(strings.ratios) as RatioChoice[]).map(
            (value) => ({ value, label: strings.ratios[value] })
          )}
          value={ratioChoice}
        />
        {ratioChoice === "custom" ? (
          <TextField
            id="ects-custom-ratio"
            label={strings.customRatioLabel}
            numeric
            onChange={setCustomRaw}
            value={customRaw}
          />
        ) : null}
        <SelectField
          id="ects-rounding"
          label={strings.roundingLabel}
          onChange={setRounding}
          options={ROUNDINGS.map((value) => ({
            value,
            label: strings.roundings[value],
          }))}
          value={rounding}
        />
      </div>

      <section aria-live="polite" className={`${RESULT_CARD} space-y-4`}>
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {credits === null ? (
          <Notice>{strings.invalid}</Notice>
        ) : (
          <>
            <StatGrid>
              <StatCard
                emphasis={from === "ects"}
                label={strings.ectsCard}
                value={fmt(credits.ects, FRACTION)}
              />
              <StatCard
                emphasis={from === "us"}
                label={strings.usCard}
                value={fmt(usShown, FRACTION)}
              />
              <StatCard
                emphasis={from === "uk"}
                label={strings.ukCard}
                value={fmt(credits.uk, FRACTION)}
              />
            </StatGrid>
            {rounding === "exact" ? null : (
              <p className="text-muted-foreground text-sm">
                {t(strings.usRoundedNote, {
                  exact: fmt(credits.us, FRACTION),
                })}
              </p>
            )}
            <p className="text-foreground text-sm">
              {t(strings.yearShare, {
                percent: fmt(shareOfYear(credits.ects), {
                  style: "percent",
                  maximumFractionDigits: 1,
                }),
              })}
            </p>
            <h3 className={WIDGET_H3}>{strings.workloadHeading}</h3>
            <DataTable
              head={[strings.colSystem, strings.colRule, strings.colHours]}
              rows={workloadRows(strings, fmt, workloadHours(credits))}
            />
            <p className="text-muted-foreground text-sm">
              {strings.workloadNote}
            </p>
            <CopyButton
              copiedLabel={strings.copied}
              label={strings.copyResult}
              small
              text={t(strings.summary, {
                ects: fmt(credits.ects, FRACTION),
                us: fmt(usShown, FRACTION),
                uk: fmt(credits.uk, FRACTION),
              })}
            />
          </>
        )}
      </section>
    </div>
  );
}
